import ComproDLS from 'comprodls-sdk';
import { reactive, watch } from 'vue';
const { checkUserAuthenticatedCache } = useOnboardingAuth();
const { setCachingStatus, getCachingStatus } = useCache();
const { logInfoToBackend } = useCustomBackendLogger();

export function usePushXService() {
  //Map containing key as orgId/extUserId and Value as grantCredentials
  //OrgId for DLS organization related events
  //ExtUserId for spaces related events
  const pushXGrantCredentials: any = new Map();

  let pushx: any;

  //Grant using ExtUser Id and Account Id
  const grantByAccountId = async (refId) => {
    if (!getCachingStatus('pushXGrantCredentialsMap')) {
      clearPushXGrantCredentialsMap();
      setCachingStatus('pushXGrantCredentialsMap', true);
    }
    if (!getPushXGrantCredentials(refId)) {
      const response = await $fetch('/apigateway/grant/account', {
        method: 'POST',
        body: { refId }
      });
      setPushXGrantCredentials(refId, response);
      return response;
    } else {
      return Promise.resolve(getPushXGrantCredentials(refId));
    }
  };
  //Grant using dls Organization
  const grantByOrgId = async (orgId: string, spaceId: string) => {
    if (!getCachingStatus('pushXGrantCredentialsMap')) {
      clearPushXGrantCredentialsMap();
      setCachingStatus('pushXGrantCredentialsMap', true);
    }
    if (!getPushXGrantCredentials(orgId)) {
      const response = await $fetch('/apigateway/grant/org', {
        method: 'POST',
        body: { orgId, spaceId }
      });
      setPushXGrantCredentials(orgId, response);
      return response;
    } else {
      return Promise.resolve(getPushXGrantCredentials(orgId));
    }
  };

  //Map key can be orgid/refId
  const connect = async (
    mapKey: string,
    pollingOptions?: {
      accountid?: string;
      pollingEndpoint?: string;
      pollingIterations?: number;
      pollingInterval?: number;
    }
  ) => {
    const { dlsAccountId: accountId } = useRuntimeConfig().public;
    /**
     * Default polling endpoint is set to getPushEvents
     * This can be overridden by passing pollingEndpoint in pollingOptions
     * If Events are not received via PushX in case of Error, polling is done to get the events via pollingEndpoint
     *
     * If rest pollingOptions(like pollingIterations) is not passed, default polling options are set by the SDK
     */
    pollingOptions = {
      accountid: accountId,
      pollingEndpoint: '/apigateway/getPushEvents',
      ...pollingOptions
    };

    const { dlsEnv: environment, dlsRealm: realm } = useRuntimeConfig().public;
    const comproDLS = ComproDLS.init(environment, realm);
    pushx = comproDLS.PushX();
    const pushXConnection = pushx.connect({
      ...getPushXGrantCredentials(mapKey),
      ...pollingOptions
    });

    return pushXConnection;
  };

  const getPushXConnection = async (
    mapKey: string,
    pollingOptions?: {
      pollingEndpoint?: string;
      pollingIterations?: number;
      pollingInterval?: number;
    }
  ) => {
    /**
     * Default polling endpoint is set to getPushEvents
     * This can be overridden by passing pollingEndpoint in pollingOptions
     * If Events are not received via PushX in case of Error, polling is done to get the events via pollingEndpoint
     *
     * If rest pollingOptions(like pollingIterations) is not passed, default polling options are set by the SDK
     */
    pollingOptions = {
      pollingEndpoint: '/apigateway/getPushEvents',
      ...pollingOptions
    };

    return connect(mapKey, pollingOptions);
  };

  const cleanup = () => {
    if (pushx) {
      pushx.cleanup();
    }
  };
  //Getters and setters for PushXGrantCredentials
  //Map key can be orgId/refId
  const setPushXGrantCredentials = (mapKey, grantCredentials) => {
    pushXGrantCredentials.set(mapKey, grantCredentials);
  };

  const getPushXGrantCredentials = (mapKey) => {
    return pushXGrantCredentials.get(mapKey);
  };
  const clearPushXGrantCredentialsMap = () => {
    pushXGrantCredentials.clear();
  };

  const setupGrantByAccountIdPushX = async (args, callback) => {
    const { loggingInfo, onPushXCreate } = args;
    const state = reactive({
      eventBody: null,
      eventContext: null,
      error: null
    });

    const { dlsAccountId: accountId } = useRuntimeConfig().public;

    const user = await checkUserAuthenticatedCache();
    cleanup();
    await grantByAccountId(user.extUserId);
    const pushXConnection = await getPushXConnection(user.extUserId);
    pushXConnection.on(
      { accountid: accountId, channel: 'refid.' + user.extUserId },
      async (eventContext) => {
        logInfoToBackend({
          event: eventContext,
          extUserId: user.extUserId,
          role: user.role,
          ...loggingInfo
        }).then();
        const eventBody = eventContext.events?.body;
        if (eventBody) {
          state.eventBody = eventBody;
          state.eventContext = eventContext;
        }
      }
    );
    pushXConnection.on(
      { channel: 'pushx_status' },
      async (subscriptionStatus) => {
        if (
          subscriptionStatus.category === 'PUSHX' &&
          subscriptionStatus.type === 'CHANNEL_SUBSCRIPTION'
        ) {
          if (subscriptionStatus.status === 'SUCCESS') {
            await onPushXCreate();
          } else if (subscriptionStatus.status === 'ERROR') {
            throw new Error(JSON.stringify(subscriptionStatus));
          }
        }
      }
    );
    //}

    watch(state, (newState) => {
      // Handle changes here
      return callback(newState.eventContext, newState.eventBody);
    });
  };
  return {
    grantByAccountId,
    grantByOrgId,
    getPushXConnection,
    cleanup,
    setupGrantByAccountIdPushX
  };
}
